// src/pages/lab/processing/components/AnalysisProcessingTab.tsx
import React, { useState, useEffect } from "react";
import { Button, Input, Space, Typography, Badge } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";

// API
import { fetchProcessingGroups } from "../../../../api/labiq/GroupProcessingApi";

// Types
import { ProcessingGroup } from "../../../../types/lab/ProtocolTypes";

// Shared components
import { DataTable, SectionCard, StatusTag } from "../../../../components/shared";

// Components
import ProtocolInstanceModal from "./components/ProtocolInstanceModal";

// Utils and hooks
import { useNotifications } from "../../../../context/NotificationContext";
import { loadUserPreferences } from "../../../../utils/userPrefs/UserPrefs";

const { Text } = Typography;

interface AnalysisProcessingTabProps {
	onRefresh: () => void;
	loading: boolean;
}

const AnalysisProcessingTab: React.FC<AnalysisProcessingTabProps> = ({ onRefresh, loading }) => {
	const [processingGroups, setProcessingGroups] = useState<ProcessingGroup[]>([]);
	const [selectedGroup, setSelectedGroup] = useState<ProcessingGroup | null>(null);
	const [instanceModalOpen, setInstanceModalOpen] = useState(false);
	const [localLoading, setLocalLoading] = useState(false);
	const PAGE_SIZE = loadUserPreferences().tableRowsPerPage;

	const { showError } = useNotifications();

	useEffect(() => {
		loadProcessingGroups();
	}, []);

	const loadProcessingGroups = async () => {
		setLocalLoading(true);
		try {
			// Load all active/pending/on_hold groups
			const response = await fetchProcessingGroups({
				per_page: 100,
			});

			// Filter for groups processing analyses (not samples)
			const analysisGroups = response.data.filter((group) => {
				// Check if protocol is NOT a first protocol
				return group.protocol && !group.protocol.is_first_protocol;
			});

			setProcessingGroups(analysisGroups);
		} catch (error) {
			showError("Failed to load processing groups");
			console.error("Processing groups load error:", error);
		} finally {
			setLocalLoading(false);
		}
	};

	const handleViewGroup = (group: ProcessingGroup) => {
		setSelectedGroup(group);
		setInstanceModalOpen(true);
	};

	const handleCloseModal = () => {
		setInstanceModalOpen(false);
		setSelectedGroup(null);
	};

	const handleRefresh = () => {
		loadProcessingGroups();
		onRefresh();
	};

	const columns: ColumnsType<ProcessingGroup> = [
		{
			title: "Group Name",
			dataIndex: "group_name",
			key: "group_name",
			fixed: "left",
			render: (text: string) => text,
		},
		{
			title: "Protocol",
			dataIndex: ["protocol", "name"],
			key: "protocol.name",
			render: (_: any, record: ProcessingGroup) => record.protocol?.name,
		},
		{
			title: "Analyses",
			key: "sample_count",
			render: (_: any, record: ProcessingGroup) => <Badge count={record.sample_count || 0} style={{ backgroundColor: "var(--mg-primary)" }} />,
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			render: (status: string) => <StatusTag status={status || "pending"} />,
		},
		{
			title: "Started",
			dataIndex: "started_at",
			key: "started_at",
			render: (date: string) => {
				if (!date) return <Text type="secondary">Not started</Text>;
				return <Text style={{ fontSize: "13px" }}>{new Date(date).toLocaleDateString()}</Text>;
			},
		}
	];

	return (
		<>
			<DataTable
				bare
				columns={columns}
				dataSource={processingGroups}
				rowKey="id"
				loading={loading || localLoading}
				onRowClick={(record) => handleViewGroup(record)}
				scrollX={1000}
				scrollY="calc(100vh - 295px)"
				pagination={{ pageSize: PAGE_SIZE }}
			/>

			{/* Protocol Instance Modal */}
			{selectedGroup && (
				<ProtocolInstanceModal
					group={selectedGroup}
					groupType="analysis"
					onClose={handleCloseModal}
					onRefresh={handleRefresh}
					isOpenModal={instanceModalOpen}
				/>
			)}
		</>
	);
};

export default AnalysisProcessingTab;
